import { PropertyOwnershipVerificationState } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyOwnershipVerificationWebApiHandler_getVerificationState } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

// The states the server lets a listing publish in (ZRM-16878).
const VERIFIED_STATES: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.COMPLETED,
  PropertyOwnershipVerificationState.APPROVED,
];

export const isOwnershipVerified = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && VERIFIED_STATES.includes(state));

// Everything else needs the step: publish is gated on the verified states, so hiding it in a blocking
// state dead-ends the landlord. The test documents the full mapping.
const STATES_WITHOUT_VERIFICATION_WORK: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.NOT_APPLICABLE,
  ...VERIFIED_STATES,
];

/** An unrecognised state reads as "show" — likelier to be blocking than resolved. */
export const shouldShowStepForState = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && !STATES_WITHOUT_VERIFICATION_WORK.includes(state));

/**
 * States `startOrResume` can act on. Narrower than `shouldShowStepForState`: the failure states still
 * show the step, but their route forward is manual review, not another inquiry.
 */
const STARTABLE_STATES: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.NOT_STARTED,
  PropertyOwnershipVerificationState.CREATED,
  PropertyOwnershipVerificationState.PENDING,
  PropertyOwnershipVerificationState.EXPIRED,
];

export const isStartableState = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && STARTABLE_STATES.includes(state));

export const isAwaitingManualReview = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  state === PropertyOwnershipVerificationState.NEEDS_REVIEW;

/**
 * A settled answer of "this listing never needed verification" — not an absent one. Surfaces
 * distinguish it from an unreadable state, which reads the same through every other flag here.
 */
export const isVerificationNotApplicable = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  state === PropertyOwnershipVerificationState.NOT_APPLICABLE;

/**
 * Submitted and still being processed. Startable, but re-offering the flow would hand the landlord
 * back an inquiry they already finished — unlike EXPIRED, which genuinely needs another run.
 */
export const isVerificationProcessing = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  state === PropertyOwnershipVerificationState.PENDING;

/**
 * Verification came back negative. `FAILED` is documented as appealable and `DECLINED` as final, but
 * there is no endpoint to request an appeal, so both read the same to the landlord for now (ZRM-17096).
 */
const FAILED_STATES: ReadonlyArray<PropertyOwnershipVerificationState> = [
  PropertyOwnershipVerificationState.FAILED,
  PropertyOwnershipVerificationState.DECLINED,
];

export const hasVerificationFailed = (state: PropertyOwnershipVerificationState | undefined): boolean =>
  Boolean(state && FAILED_STATES.includes(state));

/**
 * The server's message for a 403, which retrying never fixes — e.g. `startOrResume` rejecting anyone but
 * the listing creator (ZRM-17510). `fallback` when that message is empty; undefined for any other error.
 */
export const getForbiddenMessage = (error: unknown, fallback: string): string | undefined =>
  DatarouterError.is(error) && error.responseStatus === 403 ? error.message || fallback : undefined;

/** Anything that can change the server's applicability answer must invalidate this. */
export const OWNERSHIP_VERIFICATION_STATE_TAG = 'ownershipVerificationState';

/**
 * What the server says about a listing's ownership verification. Callers own both gates: the rollout
 * flag, and holding the lookup until the listing has a `forRentBy` — applicability is derived from it,
 * so asking earlier caches a `NOT_APPLICABLE` for a listing that qualifies a few steps later.
 */
export const useOwnershipVerificationState = ({
  listingAlias,
  enabled = true,
}: {
  listingAlias: string;
  enabled?: boolean;
}) => {
  const isEnabled = enabled && Boolean(listingAlias);

  const { data, isLoading, isFetching, isError, errorUpdateCount, refetch } =
    useRentalManagerUserPropertyOwnershipVerificationWebApiHandler_getVerificationState(
      { listingAlias },
      {
        enabled: isEnabled,
        meta: {
          tags: [OWNERSHIP_VERIFICATION_STATE_TAG, listingAlias],
          context: {
            api: 'getVerificationState',
            details: { listingAlias },
          },
        },
      },
    );

  // A disabled query still hands back its last cached answer, which outlived publishing.
  const state = isEnabled ? data?.state : undefined;

  // Sticky, because a query holding no data is reset to `pending` by every refetch (query-core's
  // `fetchState`), so `isError` alone would clear on each retry-on-mount and read as a first load
  // again — which cycled the wizard between its loading screen and the step that refetches.
  const hasFailedLookup = isEnabled && !state && (isError || errorUpdateCount > 0);

  return {
    state,
    // Fails closed: an unreadable state keeps the step, which owns the retry. Dropping it would
    // leave every route out of Review publishing an unverified listing.
    shouldShowVerificationStep: shouldShowStepForState(state) || hasFailedLookup,
    isVerified: isOwnershipVerified(state),
    isStartable: isStartableState(state),
    // Every flag above reads false before the lookup lands, indistinguishably from a real answer.
    isStateLoading: isLoading && !hasFailedLookup,
    // `isLoading` is first-load only, so a refetch after an invalidation reads as settled while the
    // previous answer is still on screen.
    isStateFetching: isFetching,
    isStateError: hasFailedLookup,
    refetchState: refetch,
  };
};
